import { useEffect, useRef } from 'react';
import type { BoardSummary, CalendarEvent } from '../lib/api';
import type { CalendarTaskDue } from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { formatDayLong, formatRelativeDay, formatTime } from './dates';

export interface DayModalProps {
  day: Date;
  events: CalendarEvent[];
  dues: CalendarTaskDue[];
  boards: BoardSummary[];
  /** A student reads the day; teachers and the owner also plan it. */
  canCreateEvent: boolean;
  canEditEvent: (event: CalendarEvent) => boolean;
  /** Students see their own state on a deadline; everyone else sees whose it is. */
  viewerIsStudent: boolean;
  viewerUserId: string | null;
  /** Owner and teachers may tick homework done from the day card. */
  canManageHomework: boolean;
  onCreateEvent: (day: Date) => void;
  onEditEvent: (event: CalendarEvent) => void;
  onOpenTask: (taskId: string) => void;
  onOpenBoard: (boardId: string) => void;
  onToggleEventDone: (event: CalendarEvent, done: boolean) => void;
  onToggleHomeworkStudent: (taskId: string, studentId: string, done: boolean) => void;
  onClose: () => void;
}

/**
 * One day, opened from the month grid.
 *
 * The grid can only ever show a few words per cell, and the question that
 * follows a click — "what exactly is due here?" — needs the whole thing:
 * subject, who it is for, and a way into the task itself.
 */
export function DayModal({
  day,
  events,
  dues,
  boards,
  canCreateEvent,
  canEditEvent,
  viewerIsStudent,
  viewerUserId,
  canManageHomework,
  onCreateEvent,
  onEditEvent,
  onOpenTask,
  onOpenBoard,
  onToggleEventDone,
  onToggleHomeworkStudent,
  onClose,
}: DayModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const boardName = (id: string | null) =>
    id ? (boards.find((board) => board.id === id)?.name ?? null) : null;

  const empty = events.length === 0 && dues.length === 0;

  return (
    <div
      className="modal-backdrop"
      onPointerDown={(e) => {
        if (!dialogRef.current?.contains(e.target as Node)) onClose();
      }}
    >
      <div
        className="day-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={formatDayLong(day)}
      >
        <header className="day-modal-head">
          <div>
            <h2>{formatRelativeDay(day)}</h2>
            <p>{formatDayLong(day)}</p>
          </div>
          <button type="button" className="icon-btn" aria-label="Закрыть" onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </header>

        <div className="day-modal-body">
          {dues.length > 0 && (
            <section className="day-section">
              <h3>
                <Icon name="clipboard" size={14} />
                Задания
                <em>{dues.length}</em>
              </h3>
              <ul className="day-dues">
                {dues.map((due) => {
                  const allDone = due.students.length > 0 && due.students.every((s) => s.done);
                  const myRow = viewerUserId
                    ? due.students.find((s) => s.userId === viewerUserId)
                    : undefined;
                  const canTick = viewerIsStudent
                    ? Boolean(myRow)
                    : canManageHomework && due.students.length > 0;
                  const checked = viewerIsStudent ? myRow?.done === true : allDone;

                  return (
                    <li key={due.id} className={allDone || due.done ? 'is-complete' : due.awaitingReview ? 'is-review' : ''}>
                      {canTick && (
                        <label className="day-done-check">
                          <input
                            type="checkbox"
                            checked={checked}
                            aria-label={checked ? 'Снять отметку о сдаче' : 'Отметить сданным'}
                            onChange={(e) => {
                              const next = e.target.checked;
                              if (viewerIsStudent && viewerUserId) {
                                onToggleHomeworkStudent(due.id, viewerUserId, next);
                                return;
                              }
                              for (const student of due.students) {
                                if (student.done !== next) {
                                  onToggleHomeworkStudent(due.id, student.userId, next);
                                }
                              }
                            }}
                          />
                        </label>
                      )}
                      <button type="button" onClick={() => onOpenTask(due.id)}>
                        <span className="day-due-head">
                          {due.subject && <span className="subject-badge">{due.subject}</span>}
                          <span className={`day-due-title ${checked ? 'is-done' : ''}`}>{due.title}</span>
                          <em className="day-due-time">{formatTime(due.dueAt)}</em>
                        </span>
                        <span className="day-due-meta">
                          {viewerIsStudent ? (
                            <span className={`task-chip ${myRow?.needsReview ? 'is-review' : myRow?.done ? 'is-done' : ''}`}>
                              {/* Review first: the student's own tick counts
                                  as done, yet is not accepted until checked. */}
                              {myRow?.needsReview ? (
                                'на проверке'
                              ) : myRow?.done ? (
                                <>
                                  <Icon name="check" size={11} />
                                  сдано
                                </>
                              ) : (
                                'не сдано'
                              )}
                            </span>
                          ) : (
                            due.students.map((student) => (
                              <span
                                key={student.userId}
                                className={`task-chip ${student.done ? 'is-done' : ''} ${student.needsReview ? 'is-review' : ''}`}
                              >
                                {student.done && <Icon name="check" size={11} />}
                                {student.name}
                              </span>
                            ))
                          )}
                          {!viewerIsStudent && due.teacherName && (
                            <em className="day-due-author">{due.teacherName}</em>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {events.length > 0 && (
            <section className="day-section">
              <h3>
                <Icon name="calendar" size={14} />
                События
                <em>{events.length}</em>
              </h3>
              <ul className="day-events">
                {events.map((event) => {
                  const linkedBoard = boardName(event.boardId);
                  const editable = canEditEvent(event);

                  return (
                    <li
                      key={event.id}
                      className={event.done ? 'is-complete' : ''}
                      style={{ borderLeftColor: event.color }}
                    >
                      <div className="day-event-head">
                        {editable && (
                          <label className="day-done-check">
                            <input
                              type="checkbox"
                              checked={event.done}
                              aria-label={event.done ? 'Снять отметку о выполнении' : 'Отметить событие выполненным'}
                              onChange={(e) => onToggleEventDone(event, e.target.checked)}
                            />
                          </label>
                        )}
                        <strong className={event.done ? 'is-done' : ''}>{event.title}</strong>
                        <span className="day-event-time">
                          {event.allDay
                            ? 'весь день'
                            : `${formatTime(event.start)}–${formatTime(event.end)}`}
                        </span>
                      </div>
                      {event.description && <p className="day-event-desc">{event.description}</p>}
                      {event.tasks.length > 0 && (
                        <p className="day-event-tasks">
                          Задачи: {event.tasks.filter((t) => t.done).length} / {event.tasks.length}
                        </p>
                      )}
                      <div className="day-event-actions">
                        {event.createdByName && (
                          <span className="day-due-author">{event.createdByName}</span>
                        )}
                        <span className="spacer" />
                        {linkedBoard && (
                          <button
                            type="button"
                            className="ghost-btn"
                            onClick={() => event.boardId && onOpenBoard(event.boardId)}
                          >
                            <Icon name="boards" size={13} />
                            {linkedBoard}
                          </button>
                        )}
                        {editable && (
                          <button
                            type="button"
                            className="ghost-btn"
                            onClick={() => onEditEvent(event)}
                          >
                            <Icon name="pencil" size={13} />
                            Изменить
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {empty && (
            <p className="day-modal-empty">
              <Icon name="calendar" size={22} />
              На этот день ничего не запланировано.
            </p>
          )}
        </div>

        <footer className="day-modal-foot">
          {canCreateEvent && (
            <button type="button" className="primary-btn" onClick={() => onCreateEvent(day)}>
              <Icon name="plus" size={15} />
              Новое событие
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="ghost-btn" onClick={onClose}>
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  );
}
